/**
 * Палітри крапок статусів і варіантів прототипу v4 (Groundwork.dc.html: STATUS_DOTS, PRIO_DOTS, ST_PAL, dotFor, shapeSpec, stripRegistry,
 * shapeFinance). Прототип — еталон там, де текст специфікації (field-types/priority: «Medium amber») з ним розходиться. Колір із метаданих
 * (статус чи варіант із `color`) завжди важливіший — ці палітри лише для значень без власного кольору.
 */

/** Палітра за порядковим номером варіанта (STATUS_DOTS прототипу): сірий, синій, бурштиновий, зелений, сланцевий, фіолетовий. */
export const statusDots: readonly string[] = [
  'oklch(0.7 0.01 260)', 'oklch(0.62 0.15 255)', 'oklch(0.75 0.14 75)', 'oklch(0.65 0.14 150)', 'oklch(0.55 0.03 260)', 'oklch(0.6 0.16 300)',
];

/** Пріоритет (PRIO_DOTS): Низький сірий · Середній синій · Високий помаранчевий · Терміновий червоний. */
export const priorityDots: Readonly<Record<string, string>> = {
  Low: 'oklch(0.7 0.01 260)', Medium: 'oklch(0.62 0.15 255)', High: 'oklch(0.72 0.15 60)', Urgent: 'oklch(0.6 0.2 27)',
};

/** Важливість (dotFor, поле severity): 1 – Критичний · 2 – Високий · 3 – Середній · 4 – Низький. */
export const severityDots: Readonly<Record<string, string>> = {
  '1 - Critical': '#b91c1c', '2 - High': '#f97316', '3 - Medium': '#eab308', '4 - Low': '#94a3b8',
};

/**
 * Статуси модулів зі специфікацією (MOD_SPEC — більшість модулів прототипу): незавершені статуси по черзі беруть ST_PAL, завершені —
 * зелений, «негативні» (відхилено, скасовано, призупинено…) — червоний.
 */
export const specOpenDots: readonly string[] = ['#94a3b8', '#1f4ebc', '#0e9aa7', '#d97706', '#7c3aed', '#2563eb'];
export const specClosedDot = '#1f8a5b';
export const specNegativeDot = '#b42318';
export const specNegative = /Reject|Cancel|Not achieved|Off track|Abandon|Suspend|Expired|Disputed|failed|Rolled back|Deprecated|Retired|Withdrawn|Left/;

/** Реєстри Верховної Ради (stripRegistry): кольори за значенням статусу; решта значень — за порядковим номером (statusDots). */
export const registryDots: Readonly<Record<string, string>> = {
  Active: '#1f8a5b', Suspended: '#d97706', Terminated: '#64748b', Planned: '#1f4ebc', Completed: '#64748b', Registered: '#1f4ebc',
  'In parliament': '#1f8a5b', 'Not in parliament': '#d97706', Dissolved: '#94a3b8',
};

/** Фінанси офісу депутата (shapeFinance): кольори за значенням статусу. */
export const financeDots: Readonly<Record<string, string>> = {
  Planned: '#1f4ebc', Active: '#1f8a5b', Closed: '#64748b', Draft: '#94a3b8', Submitted: '#1f4ebc', Approved: '#0e9aa7', Paid: '#1f8a5b',
  Rejected: '#b42318', Accrued: '#d97706', 'Not started': '#94a3b8', 'In preparation': '#d97706', Filed: '#1f8a5b',
};

/**
 * Типи записів, чиї статуси прототип фарбує не правилом специфікації: `index` — за порядковим номером (завдання, база знань, бізнес-аналіз —
 * модулі без MOD_SPEC), `registry` / `finance` — за значенням (registryDots / financeDots), `open` — правило специфікації без «завершених»
 * (специфікація прототипу не має для них закритих статусів). Другий елемент — підпис набору статусів («значення*» — закритий): за ним
 * впізнається тип, коли виклик не передав ключ типу (набір конвокацій збігається з наставництвом — перевага реєстру).
 */
export const typePalettes: Readonly<Record<string, readonly [mode: 'index' | 'registry' | 'finance' | 'open', signature: string]>> = {
  task: ['index', 'To do|In progress|Review|Done*'],
  kb: ['index', 'Draft|In review|Published*|Archived*'],
  ba: ['index', 'Draft|In review|Approved|Implemented*|Verified*|Deferred*|Proposed'],
  mandate: ['registry', 'Active|Suspended|Terminated*'],
  convocation: ['registry', 'Planned|Active|Completed*'],
  polparty: ['registry', 'Registered|In parliament|Not in parliament|Dissolved*'],
  radacmt: ['registry', 'Active|Dissolved*'],
  faction: ['registry', 'Active|Dissolved*'],
  cmtmem: ['registry', 'Active|Completed*'],
  assistant: ['registry', 'Active|Ended*'],
  mpf: ['finance', 'Planned|Active|Closed*'],
  mpfpay: ['finance', 'Planned|Accrued|Paid*'],
  mpfdec: ['finance', 'Not started|In preparation|Filed*'],
  intvmsg: ['open', 'Draft|Agreed*'],
  ldart: ['open', 'Draft|Agreed*'],
  ldprob: ['open', 'Identified|Addressed*'],
  ragtest: ['open', 'To run|Passed*|Failed*'],
};

/** Власні кольори варіантів поля в прототипі (`тип.поле` → значення → колір): посади членів комітетів. */
export const optionDots: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  'cmtmem.post': { Chair: '#1f4ebc', 'First deputy chair': '#3b6fd8', 'Deputy chair': '#6f95e3', Secretary: '#9db5ec', 'Committee member': '#94a3b8' },
};
